<template>
  <component
    v-if="isEmpty"
    ref="el"
    class="formatted-text"
    :is="tag"
    :class="{ 'is-empty': !placeholder }"
    v-bind="$attrs"
  >
    <p v-if="placeholder" class="formatted-text__placeholder">
      {{ placeholder }}
    </p>
  </component>

  <!-- eslint-disable -->
  <component
    v-else
    ref="el"
    :is="tag"
    class="formatted-text"
    v-bind="$attrs"
    v-html="cleanedHTML"
  />
  <!-- eslint-enable -->
</template>

<script setup lang="ts">
/**
 * This formatted text component can consume RAW html.
 * It uses the tiptap generation methods to strip the html down to
 * the components allowed in simcision.
 */
import { computed, ref } from "vue";
import { generateHTML, generateJSON } from "@tiptap/html";

// Extensions
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import Video from "@core/input/TextEditorVideoExtension";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";

const extensions = [
  StarterKit,
  Image,
  TaskList,
  TaskItem,
  Video,
  TextAlign.configure({
    types: ["heading", "paragraph"],
    alignments: ["start", "center", "end"],
    defaultAlignment: "start"
  }),
  TextStyleKit
];

defineOptions({
  inheritAttrs: false
});

const props = withDefaults(
  defineProps<{
    value?: string;
    placeholder?: string;
    tag?: string;
  }>(),
  {
    value: undefined,
    placeholder: "",
    tag: "div"
  }
);

const el = ref<HTMLElement | null>(null);

const cleanedHTML = computed(() => {
  if (props.value == null) return "";
  const content = generateJSON(props.value, extensions, { preserveWhitespace: true }); // Important for keeping trailing spaces in text inputs
  return generateHTML(content, extensions);
});

const isEmpty = computed(() => {
  if (!cleanedHTML.value) return true;
  const el = document.createElement("span");
  el.innerHTML = cleanedHTML.value;
  const text = el.innerText;
  if (text == "") {
    return true;
  }
  return false;
});

defineExpose({
  el,
  isEmpty
});
</script>

<style lang="scss">
.formatted-text {
  font-size: 1em;
  overflow-wrap: break-word;
  word-wrap: break-word;
  white-space: pre-wrap;
  text-wrap: stable;
  white-space-collapse: break-spaces;

  & > *:first-child {
    margin-top: 0;
  }

  p {
    line-height: 1.5;
  }
  p:empty::before {
    content: " "; // Preserve height of empty paragraphs
  }

  p:last-child {
    margin-bottom: 0;
  }

  h1 {
    font-size: 1.5em;
    line-height: 1;
  }
  h2 {
    font-size: 1.35em;
  }
  h3 {
    font-size: 1.25em;
  }
  p + h3,
  ul + h3 {
    margin-top: 3rem;
  }
  h3:first-child {
    margin-top: 1rem;
  }

  h4 {
    margin-bottom: 0.5rem;
  }
  h4 + p {
    margin-top: 0.5rem;
  }
  *:is(h1, h2, h3, h4):last-child {
    margin-bottom: 0;
  }

  h1:not(:has(> span)),
  h1 > span {
    line-height: 1.3;
  }

  img {
    max-width: 100%;
  }

  &.is-empty {
    height: 0;
    overflow: hidden;
  }

  &:not(.textviewer__field) a {
    cursor: pointer;
  }

  *:has(+ p:last-child:empty) {
    margin-bottom: 0;
  }

  &--compact {
    h1 {
      font-size: 1.25em;
      line-height: 1.2;
    }
    h2 {
      font-size: 1.125em;
      line-height: 1.15;
    }
    h3 {
      font-size: 1em;
    }
  }
}
</style>
